/**
 * Berichten aan de helden (SPEC §7.7): de uitnodiging voor de borrel, een
 * nieuwjaarsgroet, een oproep. Opstellen, een proef naar jezelf, versturen.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { api, type Doelgroep, type Mailing, type Mailinginvoer } from '../../api';
import { Bevestigknop, Datum, Knop, Laden, Melding, Veld } from '../../componenten/basis';
import { NIVEAUNAMEN, NIVEAUS, type Niveau } from '../../lib/niveau';
import type { Heldsoort } from '../../lib/vormen';

export function Berichten({ mag }: { mag: boolean }) {
  const [lijst, zetLijst] = useState<Mailing[] | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [bewerkt, zetBewerkt] = useState<Mailing | 'nieuw' | null>(null);

  useEffect(() => {
    api.haal<Mailing[]>('/api/beheer/mailingen').then(zetLijst).catch((f: Error) => zetFout(f.message));
  }, []);

  if (fout) return <Melding soort="fout">{fout}</Melding>;
  if (!lijst) return <Laden />;

  return (
    <div className="space-y-5">
      {bewerkt ? (
        <Opsteller
          bestaand={bewerkt === 'nieuw' ? null : bewerkt}
          mag={mag}
          onKlaar={(nieuw) => {
            zetLijst(nieuw);
            zetBewerkt(null);
          }}
          onSluit={() => zetBewerkt(null)}
        />
      ) : (
        mag && (
          <Knop onClick={() => zetBewerkt('nieuw')}>Nieuw bericht</Knop>
        )
      )}

      <div className="kaart overflow-x-auto">
        <table className="tabel">
          <thead>
            <tr>
              <th>Onderwerp</th>
              <th>Doelgroep</th>
              <th>Status</th>
              <th>Door</th>
            </tr>
          </thead>
          <tbody>
            {lijst.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.verstuurd_op ? (
                    <span className="font-medium">{m.onderwerp}</span>
                  ) : (
                    <button type="button" className="font-medium text-goud-300" onClick={() => zetBewerkt(m)}>
                      {m.onderwerp}
                    </button>
                  )}
                  <span className="block max-w-md truncate text-xs text-inkt-zacht">{m.tekst}</span>
                </td>
                <td className="text-inkt-zacht">{doelgroepTekst(m.doelgroep)}</td>
                <td>
                  {m.verstuurd_op ? (
                    <span className="text-groen">
                      Verstuurd <Datum waarde={m.verstuurd_op} /> aan {m.aantal_ontvangers}
                    </span>
                  ) : (
                    <span className="text-inkt-zacht">Concept</span>
                  )}
                </td>
                <td className="text-inkt-zacht">{m.verstuurd_door ?? m.aangemaakt_door}</td>
              </tr>
            ))}
            {lijst.length === 0 && (
              <tr>
                <td colSpan={4} className="py-8 text-center text-inkt-zacht">
                  Nog geen berichten. De uitnodiging voor de borrel begint hier.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function doelgroepTekst(d: Doelgroep): string {
  const delen: string[] = [];
  if (d.soorten?.length) delen.push(d.soorten.map((s) => (s === 'bedrijf' ? 'bedrijven' : 'personen')).join(' en '));
  if (d.niveaus?.length) delen.push(d.niveaus.map((n) => NIVEAUNAMEN[n].toLowerCase()).join(', '));
  return delen.length ? delen.join(' · ') : 'alle actieve helden';
}

function Opsteller({ bestaand, mag, onKlaar, onSluit }: { bestaand: Mailing | null; mag: boolean; onKlaar: (lijst: Mailing[]) => void; onSluit: () => void }) {
  const [invoer, zetInvoer] = useState<Mailinginvoer>({
    onderwerp: bestaand?.onderwerp ?? '',
    tekst: bestaand?.tekst ?? '',
    knop_tekst: bestaand?.knop_tekst ?? '',
    knop_url: bestaand?.knop_url ?? '',
    doelgroep: bestaand?.doelgroep ?? {},
  });
  const [id, zetId] = useState<string | null>(bestaand?.id ?? null);
  const [ontvangers, zetOntvangers] = useState<{ aantal: number; namen: string[] } | null>(null);
  const [melding, zetMelding] = useState<string | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [bezig, zetBezig] = useState(false);

  useEffect(() => {
    api.stuur<{ aantal: number; namen: string[] }>('/api/beheer/mailingen/ontvangers', { doelgroep: invoer.doelgroep }).then(zetOntvangers).catch(() => undefined);
  }, [invoer.doelgroep]);

  async function bewaar(): Promise<string> {
    const m = id
      ? await api.stuur<Mailing>(`/api/beheer/mailingen/${id}`, invoer, 'PUT')
      : await api.stuur<Mailing>('/api/beheer/mailingen', invoer);
    zetId(m.id);
    return m.id;
  }

  async function doe(werk: () => Promise<void>) {
    zetBezig(true);
    zetFout(null);
    zetMelding(null);
    try {
      await werk();
    } catch (f) {
      zetFout((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }

  function wisselNiveau(n: Niveau) {
    const huidig = invoer.doelgroep?.niveaus ?? [];
    const nieuw = huidig.includes(n) ? huidig.filter((x) => x !== n) : [...huidig, n];
    zetInvoer({ ...invoer, doelgroep: { ...invoer.doelgroep, niveaus: nieuw } });
  }

  function wisselSoort(s: Heldsoort) {
    const huidig = invoer.doelgroep?.soorten ?? [];
    const nieuw = huidig.includes(s) ? huidig.filter((x) => x !== s) : [...huidig, s];
    zetInvoer({ ...invoer, doelgroep: { ...invoer.doelgroep, soorten: nieuw } });
  }

  return (
    <form
      className="kaart space-y-5 p-5"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void doe(async () => {
          await bewaar();
          zetMelding('Concept bewaard.');
        });
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl">{id ? 'Bericht bewerken' : 'Nieuw bericht'}</h2>
        <Knop soort="stil" klein onClick={onSluit}>
          Sluiten
        </Knop>
      </div>
      <Veld etiket="Onderwerp" hulp="Ook de kop boven de mail.">
        <input className="veld" required value={invoer.onderwerp} onChange={(e) => zetInvoer({ ...invoer, onderwerp: e.target.value })} />
      </Veld>
      <Veld etiket="Tekst" hulp="Begint vanzelf met “Beste <voornaam>,”. Alinea’s scheiden met een witregel.">
        <textarea className="veld" rows={8} required value={invoer.tekst} onChange={(e) => zetInvoer({ ...invoer, tekst: e.target.value })} />
      </Veld>
      <div className="grid gap-4 sm:grid-cols-2">
        <Veld etiket="Knop (niet verplicht)" hulp="Bijvoorbeeld “Ik kom”.">
          <input className="veld" value={invoer.knop_tekst ?? ''} onChange={(e) => zetInvoer({ ...invoer, knop_tekst: e.target.value })} />
        </Veld>
        <Veld etiket="Adres van de knop">
          <input className="veld" placeholder="https://…" value={invoer.knop_url ?? ''} onChange={(e) => zetInvoer({ ...invoer, knop_url: e.target.value })} />
        </Veld>
      </div>
      <div>
        <span className="etiket">Doelgroep</span>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {NIVEAUS.map((n) => (
            <label key={n} className="flex items-center gap-2">
              <input type="checkbox" className="accent-goud-500" checked={invoer.doelgroep?.niveaus?.includes(n) ?? false} onChange={() => wisselNiveau(n)} />
              {NIVEAUNAMEN[n]}
            </label>
          ))}
          <span className="text-inkt-stil">|</span>
          {(['persoon', 'bedrijf'] as const).map((s) => (
            <label key={s} className="flex items-center gap-2">
              <input type="checkbox" className="accent-goud-500" checked={invoer.doelgroep?.soorten?.includes(s) ?? false} onChange={() => wisselSoort(s)} />
              {s === 'persoon' ? 'Personen' : 'Bedrijven'}
            </label>
          ))}
        </div>
        <p className="mt-2 text-sm text-inkt-zacht">
          Niets aangevinkt = alle actieve helden.{' '}
          {ontvangers && (
            <>
              Nu: <strong className="text-inkt">{ontvangers.aantal}</strong> {ontvangers.aantal === 1 ? 'ontvanger' : 'ontvangers'}
              {ontvangers.aantal > 0 && ontvangers.aantal <= 8 && <> ({ontvangers.namen.join(', ')})</>}.
            </>
          )}
        </p>
      </div>
      {fout && <Melding soort="fout">{fout}</Melding>}
      {melding && <Melding soort="goed">{melding}</Melding>}
      {mag && (
        <div className="flex flex-wrap items-center gap-3">
          <Knop type="submit" soort="stil" bezig={bezig}>
            Concept bewaren
          </Knop>
          <Knop
            soort="stil"
            bezig={bezig}
            onClick={() =>
              doe(async () => {
                const mid = await bewaar();
                const uit = await api.stuur<{ aan: string }>(`/api/beheer/mailingen/${mid}/proef`);
                zetMelding(`Proef verstuurd naar ${uit.aan}. Kijk hem na voordat je verstuurt.`);
              })
            }
          >
            Proef naar mezelf
          </Knop>
          <Bevestigknop
            soort="goud"
            klein={false}
            vraag={`Versturen aan ${ontvangers?.aantal ?? '?'} helden? Dit is niet terug te draaien.`}
            onBevestig={() =>
              doe(async () => {
                const mid = await bewaar();
                const uit = await api.stuur<{ aantal: number; mailingen: Mailing[] }>(`/api/beheer/mailingen/${mid}/versturen`);
                onKlaar(uit.mailingen);
              })
            }
          >
            Versturen
          </Bevestigknop>
        </div>
      )}
    </form>
  );
}
